import { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useLanguage } from '../../config/language';
import { useTheme } from '../../config/theme';
import { useSearchBox } from '../../hooks/useSearchBox';
import { SearchPanel } from './SearchPanel';
import { SearchOverlay } from './SearchOverlay';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

/** Desktop header search: inline pill on large screens, overlay trigger on tablets. Ctrl/⌘+K or "/" to focus. */
export const HeaderSearch = () => {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [open, setOpen] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const inputRef = useRef(null);
  const close = useCallback(() => { setOpen(false); inputRef.current?.blur(); }, []);
  const box = useSearchBox({ language, onDone: close });

  useEffect(() => {
    const onKey = (e) => {
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') || (e.key === '/' && !typing)) {
        e.preventDefault();
        if (window.innerWidth >= 1280) inputRef.current?.focus();
        else setOverlay(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <div className="relative hidden xl:block" data-testid="header-search">
        <div className={`flex items-center gap-2 h-10 rounded-full border px-3.5 transition-[width,border-color,box-shadow] duration-300 ${
          open ? 'w-80 border-[#1E9BD9]/60 shadow-[0_0_0_4px_rgba(30,155,217,0.12)]' : 'w-60 2xl:w-72'
        } ${isDarkMode ? `bg-white/[0.04] ${open ? '' : 'border-white/10 hover:border-white/20'}` : `bg-slate-50 ${open ? '' : 'border-slate-200 hover:border-slate-300'}`}`}>
          <Search className={`h-4 w-4 shrink-0 ${open ? 'text-[#1E9BD9]' : (isDarkMode ? 'text-slate-500' : 'text-slate-400')}`} />
          <input
            ref={inputRef}
            value={box.q}
            onChange={(e) => { box.setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={box.onKeyDown}
            maxLength={200}
            placeholder={isAr ? 'ابحث في كل الموقع…' : 'Search the whole site…'}
            className={`flex-1 min-w-0 h-full bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 text-sm ${isDarkMode ? 'text-white placeholder:text-slate-500' : 'text-slate-900 placeholder:text-slate-400'}`}
            data-testid="header-search-input"
            aria-label={isAr ? 'بحث' : 'Search'}
          />
          {!open && (
            <kbd className={`hidden 2xl:inline-flex items-center px-1.5 h-5 rounded-md text-[10px] font-semibold border ${isDarkMode ? 'border-white/10 text-slate-500' : 'border-slate-200 text-slate-400'}`}>
              {isMac ? '⌘K' : 'Ctrl K'}
            </kbd>
          )}
        </div>
        {open && (box.q.trim() || box.popular.length > 0) && (
          <div
            className={`absolute top-full mt-2 ${isAr ? 'right-0' : 'left-0'} w-[28rem] max-h-[70vh] overflow-y-auto rounded-2xl border shadow-2xl backdrop-blur-xl animate-slideDown z-50 ${
              isDarkMode ? 'bg-[#0F121B]/95 border-[#1E9BD9]/20' : 'bg-white/95 border-slate-200'
            }`}
            data-testid="header-search-dropdown"
          >
            <SearchPanel box={box} isAr={isAr} isDarkMode={isDarkMode} />
          </div>
        )}
      </div>

      <button type="button" onClick={() => setOverlay(true)} aria-label={isAr ? 'بحث' : 'Search'} data-testid="header-search-icon-btn"
        className={`xl:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#0f172a] hover:bg-gray-100'}`}>
        <Search className="h-5 w-5" />
      </button>
      <SearchOverlay open={overlay} onClose={() => setOverlay(false)} />
    </>
  );
};
